import type { RunAction } from '../api/client';
import type { ActionResult, Position, RunState, SpectateFrame } from '../api/types';
import { spectateRenderRun } from '../api/types';
import { monsterAssetIdForFloor } from '../assets/registry';
import { deriveGameEvents, deriveSpectateGameEvents, type EntityAnimState, type GameEvent, type PlayerAttackStyle } from '../game/gameEvents';
import { EffectsController } from './effects';

export interface GameFeelSfx {
  play(name: string): boolean;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  toggleMute(): boolean;
  setMasterVolume(v: number): void;
  getMasterVolume(): number;
  setSfxVolume(v: number): void;
  getSfxVolume(): number;
}

export interface GameFeelStage {
  setRun(run: RunState | null): void;
  playEntity(entityId: string, state: EntityAnimState | string, opts?: { facing?: 'left' | 'right'; transientMs?: number }): void;
  ensureEntity?(entityId: string, assetId: string, position: Position): void;
  spawnEffect?(name: string, position: Position): void;
  clearTransient?(): void;
}

export interface GameFeelOptions {
  stage?: GameFeelStage;
  sfx?: GameFeelSfx | null;
  effects?: EffectsController;
  now?: () => number;
  reducedMotion?: boolean;
}

export interface ActionVisualContext {
  prev?: RunState;
}

export interface ActionPresentationOptions {
  playerAttackStyle?: PlayerAttackStyle;
}

const PLAYER_ENTITY_ID = 'player';
const enemyEntityId = (enemyId: string): string => `enemy:${enemyId}`;

function targetEntityId(event: Extract<GameEvent, { kind: 'damage' | 'flash' }>): string {
  return event.targetType === 'player' ? PLAYER_ENTITY_ID : enemyEntityId(event.enemyId ?? 'missing');
}

function previousEnemyPosition(prev: RunState | undefined, enemyId: string): Position | null {
  return prev?.floorState?.enemies?.find((enemy) => enemy.id === enemyId)?.position ?? null;
}

function monsterAssetIdForRun(run: RunState | undefined): string {
  return monsterAssetIdForFloor(run?.floorState?.floor ?? run?.floor ?? 1);
}

function knockbackDirection(prev: RunState | undefined, target: Position): { x: number; y: number } {
  const player = prev?.position;
  if (!player) return { x: 0, y: -1 };
  const dx = Math.sign(target.x - player.x);
  const dy = Math.sign(target.y - player.y);
  if (dx !== 0 || dy !== 0) return { x: dx, y: dy };
  return { x: 0, y: -1 };
}

/**
 * Facade that coordinates derived run events with animation, SFX, and effects.
 *
 * The backend remains server-authoritative. This facade only interprets the
 * already-confirmed action result and routes it to the client-only presentation
 * layer. Browser-specific canvas/audio loading is injected through the stage/SFX
 * interfaces so the orchestration stays unit-testable.
 */
export class GameFeel {
  readonly effects: EffectsController;
  private readonly now: () => number;
  private stage: GameFeelStage | null;
  private sfx: GameFeelSfx | null;
  private recentEvents: GameEvent[] = [];

  constructor(options: GameFeelOptions = {}) {
    this.effects = options.effects ?? new EffectsController({ reducedMotion: options.reducedMotion });
    this.now = options.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.stage = options.stage ?? null;
    this.sfx = options.sfx ?? null;
  }

  setStage(stage: GameFeelStage | null): void {
    this.stage = stage;
  }

  setSfx(sfx: GameFeelSfx | null): void {
    this.sfx = sfx;
  }

  getSfx(): GameFeelSfx | null {
    return this.sfx;
  }

  getRecentEvents(): GameEvent[] {
    return [...this.recentEvents];
  }

  refresh(run: RunState | null): void {
    this.stage?.setRun(run);
  }

  handleActionResult(action: RunAction, prev: RunState, result: ActionResult, options: ActionPresentationOptions = {}): GameEvent[] {
    const events = deriveGameEvents({ action, prev, result, playerAttackStyle: options.playerAttackStyle });
    this.applyEvents(events, { prev });
    return events;
  }

  handleSpectateFrame(prev: SpectateFrame | null, current: SpectateFrame): GameEvent[] {
    const events = deriveSpectateGameEvents({ prev, current });
    this.applyEvents(events, { prev: prev ? spectateRenderRun(prev.run) : undefined });
    return events;
  }

  clear(): void {
    this.recentEvents = [];
    this.effects.clear();
    this.stage?.clearTransient?.();
  }

  applyEvents(events: GameEvent[], context: ActionVisualContext = {}): void {
    const now = this.now();
    this.recentEvents = events;
    for (const event of events) this.applyEvent(event, now, context.prev);
  }

  private applyEvent(event: GameEvent, now: number, prev: RunState | undefined): void {
    switch (event.kind) {
      case 'playerAnim':
        this.stage?.playEntity(PLAYER_ENTITY_ID, event.state, {
          ...(event.facing ? { facing: event.facing } : {}),
          ...(event.state === 'walk' ? { transientMs: 220 } : {}),
        });
        return;

      case 'enemyAnim': {
        const entityId = enemyEntityId(event.enemyId);
        if (event.state === 'death') {
          const pos = previousEnemyPosition(prev, event.enemyId);
          if (pos) this.stage?.ensureEntity?.(entityId, monsterAssetIdForRun(prev), pos);
        }
        this.stage?.playEntity(entityId, event.state);
        return;
      }

      case 'damage': {
        const entityId = targetEntityId(event);
        this.effects.floatingText(`-${event.amount}`, {
          x: event.pos.x,
          y: event.pos.y,
          durationMs: 800,
          now,
          color: event.targetType === 'player' ? '#c46a5a' : '#e8e0d2',
        });
        const dir = knockbackDirection(prev, event.pos);
        this.effects.knockback(entityId, { dirX: dir.x, dirY: dir.y, distance: 8, durationMs: 130, now });
        return;
      }

      case 'flash':
        this.effects.flash(targetEntityId(event), { durationMs: 180, now });
        return;

      case 'sfx':
        this.sfx?.play(event.name);
        return;

      case 'effect':
        this.stage?.spawnEffect?.(event.name, event.pos);
        return;

      case 'sparkle':
        this.effects.spark({ x: event.pos.x, y: event.pos.y, durationMs: 420, now });
        return;

      case 'shake':
        this.effects.shake({ intensity: event.intensity, durationMs: 160, now });
        return;

      case 'hitStop':
        this.effects.hitStop({ durationMs: event.durationMs, now });
        return;

      case 'pulse':
        this.effects.pulse(event.name, { durationMs: 520, now });
        return;
    }
  }
}
